import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Ticket, Hash, Users as UsersIcon, Search, Upload, AlertTriangle, FileText, X } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function normalizePhone(phone) {
  phone = phone.trim().replace(/[\s-]/g, '');
  if (phone.startsWith('+44')) phone = '0' + phone.slice(3);
  else if (phone.startsWith('0044')) phone = '0' + phone.slice(4);
  else if (phone.startsWith('44') && phone.length >= 11) phone = '0' + phone.slice(2);
  return phone;
}

const DistributeTicketsForm = ({ users, draws, onTicketsDistributed }) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedDraw, setSelectedDraw] = useState(() => {
    const saved = localStorage.getItem('lastSelectedDraw');
    return saved || '';
  });
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userQuantityMap, setUserQuantityMap] = useState({});
  const [uploadedFileNames, setUploadedFileNames] = useState([]);
  const [unmatchedNumbers, setUnmatchedNumbers] = useState([]);
  const [assigningIndex, setAssigningIndex] = useState(null);
  const [assignSearch, setAssignSearch] = useState('');
  const [creatingNew, setCreatingNew] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const fileInputRef = useRef(null);

  const toggleUser = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
    if (selectedUsers.includes(userId)) {
      setUserQuantityMap(prev => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    }
  };

  const selectAllUsers = () => {
    setSelectedUsers(users.map(u => u.id));
  };

  const clearAllUsers = () => {
    setSelectedUsers([]);
    setUserQuantityMap({});
    setUploadedFileNames([]);
    setUnmatchedNumbers([]);
    setAssigningIndex(null);
    setAssignSearch('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const assignUnmatchedToUser = async (unmatchedIdx, user) => {
    const item = unmatchedNumbers[unmatchedIdx];
    if (!item) return;

    // Save the number to the user's contact book
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/users/${user.id}/add-number`,
        { phone: item.phone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      // Non-blocking — still assign even if save fails
    }

    setSelectedUsers(prev => prev.includes(user.id) ? prev : [...prev, user.id]);
    setUserQuantityMap(prev => ({ ...prev, [user.id]: (prev[user.id] || 0) + item.qty }));
    setUnmatchedNumbers(prev => prev.filter((_, i) => i !== unmatchedIdx));
    setAssigningIndex(null);
    setAssignSearch('');
    toast.success(`${item.phone} assigned to ${user.name} and saved to their contact`);
  };

  const createNewUserAndAssign = async (unmatchedIdx) => {
    const item = unmatchedNumbers[unmatchedIdx];
    if (!item || !newUserName.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/admin/users/create`,
        { name: newUserName.trim(), phone: item.phone },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newUser = res.data.user;
      setSelectedUsers(prev => [...prev, newUser.id]);
      setUserQuantityMap(prev => ({ ...prev, [newUser.id]: item.qty }));
      setUnmatchedNumbers(prev => prev.filter((_, i) => i !== unmatchedIdx));
      setAssigningIndex(null);
      setAssignSearch('');
      setCreatingNew(false);
      setNewUserName('');
      toast.success(`Created ${newUser.name} (${newUser.whatsapp_number}) — temp password: ${newUser.default_password}`);
      onTicketsDistributed(); // refresh users list
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const filteredUsers = users
    .filter(user =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.nickname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.whatsapp_number.includes(searchQuery)
    )
    .sort((a, b) => {
      const aSelected = selectedUsers.includes(a.id) ? 0 : 1;
      const bSelected = selectedUsers.includes(b.id) ? 0 : 1;
      if (aSelected !== bSelected) return aSelected - bSelected;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const phoneToUser = {};
    users.forEach(u => {
      phoneToUser[normalizePhone(u.whatsapp_number)] = u;
      if (u.additional_numbers) {
        u.additional_numbers.forEach(num => {
          phoneToUser[normalizePhone(num)] = u;
        });
      }
    });

    const allMatched = [...selectedUsers];
    const allQtyMap = { ...userQuantityMap };
    const allUnmatched = [...unmatchedNumbers];
    const allFileNames = [...uploadedFileNames];
    let filesProcessed = 0;

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());

        lines.forEach(line => {
          const trimmed = line.trim();
          if (!trimmed) return;
          const lastSpace = trimmed.lastIndexOf(' ');
          if (lastSpace === -1) return;
          const rawPhone = trimmed.substring(0, lastSpace).replace(/[\s-]/g, '');
          const qty = parseInt(trimmed.substring(lastSpace + 1)) || 1;
          const normalized = normalizePhone(rawPhone);

          const user = phoneToUser[normalized];
          if (user) {
            if (!allMatched.includes(user.id)) {
              allMatched.push(user.id);
            }
            allQtyMap[user.id] = (allQtyMap[user.id] || 0) + qty;
          } else {
            if (!allUnmatched.some(u => u.phone === rawPhone)) {
              allUnmatched.push({ phone: rawPhone, qty });
            }
          }
        });

        allFileNames.push(file.name);
        filesProcessed++;

        if (filesProcessed === files.length) {
          setSelectedUsers(allMatched);
          setUserQuantityMap(allQtyMap);
          setUploadedFileNames(allFileNames);
          setUnmatchedNumbers(allUnmatched);

          const newMatches = allMatched.length - selectedUsers.length;
          if (newMatches > 0) {
            toast.success(`${newMatches} user(s) matched from ${files.length} file(s)`);
          }
          if (allUnmatched.length > unmatchedNumbers.length) {
            toast.warning(`${allUnmatched.length - unmatchedNumbers.length} new unmatched number(s)`);
          }
          if (newMatches === 0 && allUnmatched.length === unmatchedNumbers.length) {
            toast.error('No new matching users found');
          }
        }
      };
      reader.readAsText(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    if (!selectedDraw) {
      toast.error('Please select a draw');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const hasFileQuantities = Object.keys(userQuantityMap).length > 0;

      if (hasFileQuantities) {
        const groups = {};
        selectedUsers.forEach(uid => {
          const qty = userQuantityMap[uid] || quantity;
          if (!groups[qty]) groups[qty] = [];
          groups[qty].push(uid);
        });

        let totalTickets = 0;
        for (const [qty, userIds] of Object.entries(groups)) {
          await axios.post(
            `${API}/admin/tickets/distribute`,
            { user_ids: userIds, draw_id: selectedDraw, quantity: parseInt(qty) },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          totalTickets += parseInt(qty) * userIds.length;
        }
        toast.success(`${totalTickets} ticket(s) distributed to ${selectedUsers.length} user(s) successfully!`);
      } else {
        await axios.post(
          `${API}/admin/tickets/distribute`,
          { user_ids: selectedUsers, draw_id: selectedDraw, quantity },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const totalTickets = quantity * selectedUsers.length;
        toast.success(`${totalTickets} ticket(s) distributed to ${selectedUsers.length} user(s) successfully! (${quantity} each)`);
      }

      setSelectedUsers([]);
      setQuantity(1);
      setUserQuantityMap({});
      setUploadedFileNames([]);
      setUnmatchedNumbers([]);
      setAssigningIndex(null);
      setAssignSearch('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      onTicketsDistributed();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to distribute tickets');
    } finally {
      setLoading(false);
    }
  };

  const hasFileQuantities = Object.keys(userQuantityMap).length > 0;

  // Calculate distribution summary
  const distributionSummary = (() => {
    if (selectedUsers.length === 0) return null;
    if (hasFileQuantities) {
      const groups = {};
      let total = 0;
      selectedUsers.forEach(uid => {
        const qty = userQuantityMap[uid] || 1;
        if (!groups[qty]) groups[qty] = 0;
        groups[qty]++;
        total += qty;
      });
      return { groups, total, userCount: selectedUsers.length };
    }
    return { groups: { [quantity]: selectedUsers.length }, total: quantity * selectedUsers.length, userCount: selectedUsers.length };
  })();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-auto max-w-2xl rounded-2xl border border-white/[0.06] bg-[#18181b] p-5 sm:rounded-3xl sm:p-8"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-[#A855F7]/20 p-3 rounded-xl">
          <Ticket className="w-6 h-6 text-[#A855F7]" />
        </div>
        <div>
          <h2 className="font-heading text-lg font-semibold text-white sm:text-xl">Distribute tickets</h2>
          <p className="text-[#a1a1aa] text-sm">Assign raffle tickets to participants</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Draw Selection — Button Tabs */}
        <div className="space-y-2">
          <Label className="text-[#fafafa]">Select Draw</Label>
          {draws.length === 0 ? (
            <p className="text-[#a1a1aa] text-sm py-3">No pending draws available</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="draw-button-tabs">
              {draws.map((draw) => (
                <button
                  key={draw.id}
                  type="button"
                  data-testid={`draw-tab-${draw.id}`}
                  onClick={() => { setSelectedDraw(draw.id); localStorage.setItem('lastSelectedDraw', draw.id); }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedDraw === draw.id
                      ? 'bg-[#A855F7] text-white shadow-lg shadow-[#A855F7]/25'
                      : 'bg-[#27272a] text-[#a1a1aa] hover:bg-[#3f3f46] hover:text-white'
                  }`}
                >
                  {draw.title}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Users Section */}
        <div className="space-y-2">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Label className="text-[#fafafa]">Select Users</Label>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                size="sm"
                data-testid="upload-file-btn"
                className="h-9 rounded-full border-[#22c55e]/60 text-[#22c55e] hover:bg-[#22c55e] hover:text-black text-xs gap-1"
              >
                <Upload className="w-3 h-3" />
                Upload File
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.csv,.text"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                data-testid="file-input"
              />
              <Button
                type="button"
                onClick={selectAllUsers}
                variant="outline"
                size="sm"
                className="h-9 rounded-full border-[#A855F7]/60 text-[#A855F7] hover:bg-[#A855F7] hover:text-white text-xs"
              >
                Select All
              </Button>
              <Button
                type="button"
                onClick={clearAllUsers}
                variant="outline"
                size="sm"
                className="h-9 rounded-full border-white/[0.12] text-[#a1a1aa] hover:bg-[#52525b] hover:text-white text-xs"
              >
                Clear All
              </Button>
            </div>
          </div>

          {/* Uploaded file badges */}
          <AnimatePresence>
            {uploadedFileNames.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-center gap-2 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-lg px-3 py-2 flex-wrap"
              >
                <FileText className="w-4 h-4 text-[#22c55e]" />
                <span className="text-[#22c55e] text-sm flex-1">{uploadedFileNames.join(', ')}</span>
                <button
                  type="button"
                  onClick={clearAllUsers}
                  className="text-[#a1a1aa] hover:text-white"
                >
                  <X className="w-3 h-3" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Unmatched phone numbers warning */}
          <AnimatePresence>
            {unmatchedNumbers.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-[#EAB308]/10 border border-[#EAB308]/30 rounded-lg px-4 py-3"
                data-testid="unmatched-warning"
              >
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-[#EAB308]" />
                  <span className="text-[#EAB308] text-sm font-medium">
                    {unmatchedNumbers.length} number(s) not found — tap to assign:
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {unmatchedNumbers.map((item, i) => (
                    <div key={i} className="relative">
                      <button
                        type="button"
                        onClick={() => { setAssigningIndex(assigningIndex === i ? null : i); setAssignSearch(''); setCreatingNew(false); }}
                        className={`text-xs px-2 py-1 rounded cursor-pointer transition-all ${
                          assigningIndex === i
                            ? 'bg-[#A855F7]/30 text-[#A855F7] ring-1 ring-[#A855F7]'
                            : 'bg-[#EAB308]/20 text-[#EAB308] hover:bg-[#EAB308]/40'
                        }`}
                        data-testid={`unmatched-number-${i}`}
                      >
                        {item.phone} (x{item.qty})
                      </button>

                      {assigningIndex === i && (
                        <div className="absolute z-50 top-full left-0 mt-1 w-64 bg-[#18181b] border border-[#3f3f46] rounded-xl shadow-xl overflow-hidden">
                          {!creatingNew ? (
                            <>
                              <div className="p-2 border-b border-[#27272a]">
                                <Input
                                  type="text"
                                  placeholder="Search user..."
                                  value={assignSearch}
                                  onChange={(e) => setAssignSearch(e.target.value)}
                                  autoFocus
                                  className="bg-[#27272a] border-[#27272a] text-white placeholder:text-[#52525b] text-xs h-8"
                                />
                              </div>
                              <div className="max-h-40 overflow-y-auto">
                                {users
                                  .filter(u =>
                                    u.name.toLowerCase().includes(assignSearch.toLowerCase()) ||
                                    u.nickname?.toLowerCase().includes(assignSearch.toLowerCase()) ||
                                    u.whatsapp_number.includes(assignSearch)
                                  )
                                  .slice(0, 8)
                                  .map(u => (
                                    <button
                                      key={u.id}
                                      type="button"
                                      onClick={() => assignUnmatchedToUser(i, u)}
                                      className="w-full text-left px-3 py-2 hover:bg-[#27272a] transition-colors"
                                      data-testid={`assign-user-${u.id}`}
                                    >
                                      <div className="text-white text-xs font-medium">{u.name}</div>
                                      <div className="text-[#a1a1aa] text-xs">{u.whatsapp_number}</div>
                                    </button>
                                  ))}
                              </div>
                              <button
                                type="button"
                                onClick={() => { setCreatingNew(true); setNewUserName(''); }}
                                className="w-full text-left px-3 py-2 border-t border-[#27272a] hover:bg-[#22c55e]/10 transition-colors"
                                data-testid={`create-new-user-btn-${i}`}
                              >
                                <div className="text-[#22c55e] text-xs font-medium">+ Create New User</div>
                                <div className="text-[#a1a1aa] text-xs">Add {item.phone} as a new contact</div>
                              </button>
                            </>
                          ) : (
                            <div className="p-3 space-y-2">
                              <div className="text-[#a1a1aa] text-xs">New user for {item.phone}</div>
                              <Input
                                type="text"
                                placeholder="Enter name..."
                                value={newUserName}
                                onChange={(e) => setNewUserName(e.target.value)}
                                autoFocus
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); createNewUserAndAssign(i); } }}
                                className="bg-[#27272a] border-[#27272a] text-white placeholder:text-[#52525b] text-xs h-8"
                                data-testid={`new-user-name-input-${i}`}
                              />
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => createNewUserAndAssign(i)}
                                  disabled={!newUserName.trim()}
                                  className="bg-[#22c55e] hover:bg-[#16a34a] text-white text-xs h-7 flex-1"
                                  data-testid={`confirm-create-user-btn-${i}`}
                                >
                                  Create
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setCreatingNew(false)}
                                  className="border-[#3f3f46] text-[#a1a1aa] text-xs h-7"
                                >
                                  Back
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Search Filter */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525b]" />
            <Input
              type="text"
              placeholder="Search by name, nickname, or WhatsApp..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-[#27272a] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#A855F7] pl-10"
            />
          </div>

          <div className="bg-[#27272a] border border-[#27272a] rounded-xl p-4 max-h-64 overflow-y-auto space-y-2">
            {filteredUsers.length === 0 ? (
              <p className="text-[#a1a1aa] text-sm text-center py-4">
                {searchQuery ? 'No users found matching your search' : 'No users available'}
              </p>
            ) : (
              filteredUsers.map((user) => {
                const isSelected = selectedUsers.includes(user.id);
                const fileQty = userQuantityMap[user.id];
                return (
                  <label
                    key={user.id}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
                      isSelected ? 'bg-[#A855F7]/10 border border-[#A855F7]/30' : 'hover:bg-[#18181b] border border-transparent'
                    }`}
                    htmlFor={`user-${user.id}`}
                  >
                    <Checkbox
                      id={`user-${user.id}`}
                      checked={isSelected}
                      onCheckedChange={() => toggleUser(user.id)}
                      className="border-[#52525b] data-[state=checked]:bg-[#A855F7] data-[state=checked]:border-[#A855F7]"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{user.name}</span>
                        {user.nickname && (
                          <span className="text-[#EAB308] text-sm">({user.nickname})</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-[#a1a1aa]">{user.whatsapp_number}</span>
                        {user.active_tickets !== undefined && (
                          <span className="text-[#A855F7] flex items-center gap-1">
                            <Ticket className="w-3 h-3" />
                            {user.active_tickets} active
                          </span>
                        )}
                      </div>
                    </div>
                    {fileQty && (
                      <span className="bg-[#22c55e]/20 text-[#22c55e] text-xs font-bold px-2 py-1 rounded-full">
                        x{fileQty}
                      </span>
                    )}
                  </label>
                );
              })
            )}
          </div>

          {selectedUsers.length > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <UsersIcon className="w-4 h-4 text-[#A855F7]" />
              <span className="text-[#a1a1aa]">
                {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
                {hasFileQuantities && ' (quantities from file)'}
              </span>
            </div>
          )}
        </div>

        {/* Quantity — only shown when NOT using file quantities */}
        {!hasFileQuantities && (
          <div className="space-y-2">
            <Label htmlFor="quantity" className="text-[#fafafa]">Number of Tickets (per user)</Label>
            <div className="relative">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525b]" />
              <Input
                id="quantity"
                data-testid="ticket-quantity-input"
                type="number"
                min="1"
                max="100"
                placeholder="How many tickets?"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                className="bg-[#27272a] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#A855F7] pl-10"
              />
            </div>
            <p className="text-xs text-[#a1a1aa]">
              Ticket numbers will be automatically assigned sequentially
            </p>
          </div>
        )}

        {/* Distribution Summary */}
        <AnimatePresence>
          {distributionSummary && selectedDraw && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-[#A855F7]/10 border border-[#A855F7]/30 rounded-xl px-4 py-3"
              data-testid="distribution-summary"
            >
              <p className="text-white text-sm font-medium mb-1">Distribution Summary</p>
              <div className="flex flex-wrap gap-3 text-sm">
                {Object.entries(distributionSummary.groups).map(([qty, count]) => (
                  <span key={qty} className="text-[#a1a1aa]">
                    <span className="text-white font-medium">{count}</span> user{count > 1 ? 's' : ''} x <span className="text-[#22c55e] font-bold">{qty} ticket{parseInt(qty) > 1 ? 's' : ''}</span>
                  </span>
                ))}
                <span className="text-[#A855F7] font-bold">
                  = {distributionSummary.total} total tickets
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <Button
          type="submit"
          data-testid="distribute-tickets-submit-btn"
          disabled={loading || selectedUsers.length === 0 || !selectedDraw}
          className="w-full bg-[#A855F7] hover:bg-[#9333EA] text-white font-semibold rounded-full h-12 mt-6"
        >
          {loading
            ? 'Distributing...'
            : distributionSummary
              ? `Distribute ${distributionSummary.total} Ticket${distributionSummary.total !== 1 ? 's' : ''} to ${selectedUsers.length} User${selectedUsers.length !== 1 ? 's' : ''}`
              : `Distribute to ${selectedUsers.length} User${selectedUsers.length !== 1 ? 's' : ''}`
          }
        </Button>
      </form>
    </motion.div>
  );
};

export default DistributeTicketsForm;
